import {
  createContext,
  useContext,
  ReactNode,
  useState,
  useEffect,
  useRef,
  useCallback,
} from "react";
import { useSocket, useSocketEvent } from "socket.io-react-hook";
import { _getToken } from "src/Config/axiosApi";
import { AUTH_DISABLED } from "src/App/Providers";
import { PositionsGroupedByPair } from "../types";

interface PositionStats {
  total: number;
  open: number;
  closed: number;
  expired: number;
  totalInvested: number;
  totalUnrealizedPnl: number;
}

interface PositionsSnapshot {
  type: "POSITIONS_SNAPSHOT" | "POSITIONS_UPDATE";
  grouped: PositionsGroupedByPair[];
  stats: PositionStats;
  timestamp: number;
}

interface PositionsContextProps {
  groups: PositionsGroupedByPair[];
  unmatched: PositionsGroupedByPair[];
  stats: PositionStats | null;
  isConnected: boolean;
  triggerSync: () => void;
  isSyncing: boolean;
}

const PositionsContext = createContext<PositionsContextProps | undefined>(
  undefined
);

export const PositionsProvider = ({ children }: { children: ReactNode }) => {
  const accessToken = _getToken();

  const [groups, setGroups] = useState<PositionsGroupedByPair[]>([]);
  const [unmatched, setUnmatched] = useState<PositionsGroupedByPair[]>([]);
  const [stats, setStats] = useState<PositionStats | null>(null);
  const [isSyncing, setIsSyncing] = useState(false);

  const { socket } = useSocket(
    import.meta.env.VITE_PREDICTIONS_WS_URL + "/predictions",
    {
      enabled: AUTH_DISABLED || !!accessToken,
      auth: (cb: (data: { token: string }) => void) =>
        cb({ token: _getToken() }),
    }
  );

  const { lastMessage: snapshotMsg } = useSocketEvent<PositionsSnapshot>(
    socket,
    "positions_snapshot"
  );

  const { lastMessage: updateMsg } = useSocketEvent<PositionsSnapshot>(
    socket,
    "positions_update"
  );

  const hasSubscribed = useRef(false);

  useEffect(() => {
    if (!socket.connected) {
      hasSubscribed.current = false;
      return;
    }
    if (!hasSubscribed.current) {
      socket.emit("subscribe_predictions");
      hasSubscribed.current = true;
    }
  }, [socket.connected]);

  const applyData = useCallback((msg: PositionsSnapshot) => {
    if (!msg?.grouped) return;
    setGroups(msg.grouped.filter((g) => g._id != null));
    setUnmatched(msg.grouped.filter((g) => g._id == null));
    if (msg.stats) setStats(msg.stats);
    setIsSyncing(false);
  }, []);

  useEffect(() => {
    if (snapshotMsg) applyData(snapshotMsg);
  }, [snapshotMsg, applyData]);

  useEffect(() => {
    if (updateMsg) applyData(updateMsg);
  }, [updateMsg, applyData]);

  const triggerSync = useCallback(() => {
    setIsSyncing(true);
    socket.emit("sync_positions");
  }, [socket]);

  const isConnected = socket.connected;

  return (
    <PositionsContext.Provider
      value={{ groups, unmatched, stats, isConnected, triggerSync, isSyncing }}
    >
      {children}
    </PositionsContext.Provider>
  );
};

export const usePositions = () => {
  const context = useContext(PositionsContext);
  if (!context) {
    throw new Error("usePositions must be used within a PositionsProvider");
  }
  return context;
};
